import { ButtonBase, styled } from '@mui/material';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from 'shell/components/CircularProgress';
import Dropdown from 'shell/components/Dropdown';
import Icon from 'shell/components/Icon';
import IconButton from 'shell/components/IconButton';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import { downloadFileFromURL, getAbsoluteURL, getS3FileURL } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const SIZES = ['small', 'medium'];
const TYPES = ['file', 'link'];

const CustomButtonBase = styled(ButtonBase)(({ $disabled, theme }) => {
    return {
        border: `1px solid ${theme.palette.ensightful.neutral[100]}`,
        borderRadius: '8px',
        width: '100%',
        padding: theme.spacing(3),
        background: theme.palette.ensightful.neutral[50],
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
        '&:hover': {
            borderColor: theme.palette.ensightful.neutral[200],
            color: theme.palette.ensightful.neutral[900],
        },
        '&:active': {
            borderColor: theme.palette.ensightful.neutral[300],
        },
        ...($disabled && {
            pointerEvents: 'none',
        }),
    };
});

const CustomIcon = styled(Icon)(({ size, theme }) => {
    return {
        alignSelf: 'center',
        fontSize: '1.5rem',
        color: theme.palette.ensightful.neutral[900],
        ...(size === 'medium' && {
            alignSelf: 'flex-start',
            fontSize: '2rem',
        }),
    };
});

const DeleteIcon = styled(Icon)(({ size, theme }) => {
    return {
        alignSelf: 'center',
        borderRadius: '4px',
        padding: size === 'small' ? theme.spacing(1.5) : theme.spacing(2),
        fontSize: '1.5rem',
        background: theme.palette.ensightful.neutral[500],
        color: theme.palette.ensightful.shades[0],
        ...(size === 'medium' && {
            alignSelf: 'flex-start',
            fontSize: '2rem',
        }),
    };
});

const CustomStack = styled(Stack)(() => {
    return {
        flex: 1,
        minWidth: 0,
    };
});

const CustomText = styled(Text)(({ type }) => {
    return {
        ...(type === 'link' && {
            textDecoration: 'underline',
        }),
    };
});

const CustomCircularProgress = styled(CircularProgress)(() => {
    return {
        width: '1.5rem !important',
        height: '1.5rem !important',
    };
});

export const getIconKind = (type, resource) => {
    if (type === 'link') return 'link';

    const mimeType = resource?.data?.type;
    if (!mimeType) return 'fileUnknown';

    const [major, minor] = mimeType.split('/');

    switch (major) {
        case 'video':
            return 'fileTypeVideo';
        case 'image':
            return 'fileImg';
        case 'text':
            if (minor === 'csv') return 'fileCSV';
            return 'fileUnknown';
        case 'application':
            switch (mimeType) {
                case 'application/pdf':
                    return 'filePDF';
                case 'application/msword':
                case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                    return 'fileWord';
                case 'application/vnd.ms-excel':
                case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                    return 'fileExcel';
                case 'application/vnd.ms-powerpoint':
                case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
                    return 'filePPT';
                case 'application/zip':
                    return 'fileZIP';
                default:
                    return 'fileUnknown';
            }
        default:
            return 'fileUnknown';
    }
};

export const getResourceName = (type, resource) => {
    if (type === 'link') return resource.name || resource.url;
    return resource.data.name;
};

export const getResourceType = (type, resource, t) => {
    if (type === 'link') return 'Link';

    const mimeType = resource?.data?.type;
    if (!mimeType) return 'Unknown';

    const [major, minor] = mimeType.split('/');

    switch (major) {
        case 'video':
            return t('resource-type.video', 'Video File');
        case 'image':
            return t('resource-type.image', 'Image');
        case 'text':
            if (minor === 'csv') return t('resource-type.csv', 'CSV');
            break;
        case 'application':
            switch (mimeType) {
                case 'application/pdf':
                    return t('resource-type.pdf', 'PDF');
                case 'application/msword':
                case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                    return t('resource-type.doc', 'DOC');
                case 'application/vnd.ms-excel':
                case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                    return t('resource-type.excel', 'Excel');
                case 'application/vnd.ms-powerpoint':
                case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
                    return t('resource-type.ppt', 'Power Point');
                case 'application/zip':
                    return t('resource-type.zip', 'ZIP File');
                default:
                    return t('resource-type.unknown', 'Unknown');
            }
        default:
            return t('resource-type.unknown', 'Unknown');
    }
};

export default forwardRef(({ description = null, options, resource, size, type, onClick, onSelect, ...rest }, ref) => {
    const { t } = useTranslation();
    if (!SIZES.includes(size)) throw new Error(`Shell: Invalid size "${size}" in component ResourceChip.`);
    if (!TYPES.includes(type)) throw new Error(`Shell: Invalid type "${type}" in component ResourceChip.`);

    const textVariant = {
        small: 'p3',
        medium: 'p2',
    }[size];

    if (type === 'link') {
        rest.href = getAbsoluteURL(resource.url);
        rest.rel = 'noopener noreferrer';
        rest.target = '_blank';
    }

    const isDisabled = !resource.active || resource.loading;

    return (
        <CustomButtonBase
            ref={ref}
            {...rest}
            component={'div'}
            $disabled={isDisabled}
            role={isDisabled ? undefined : 'presentation'}
            onClick={(e) => {
                if (isDisabled) {
                    e.preventDefault();
                } else {
                    if (onClick) {
                        e.preventDefault();
                        onClick(e);
                    } else {
                        if (type === 'file') {
                            downloadFileFromURL(getS3FileURL(resource.data), resource.data.name);
                        }
                    }
                }
            }}
        >
            <Stack sx={{ width: '100%', minWidth: 0 }} alignItems={'center'} direction={'row'} spacing={3}>
                {resource.active ? (
                    <CustomIcon kind={getIconKind(type, resource)} size={size} />
                ) : (
                    <DeleteIcon kind={'delete'} size={size} />
                )}
                <CustomStack alignItems={'flex-start'}>
                    {resource.active ? (
                        <>
                            <CustomText noWrap type={type} variant={textVariant}>
                                {getResourceName(type, resource)}
                            </CustomText>
                            {description ? (
                                typeof description === 'string' ? (
                                    <Text color={'light'} component={'i'} variant={textVariant}>
                                        {description}
                                    </Text>
                                ) : (
                                    description
                                )
                            ) : (
                                <Text color={'light'} variant={textVariant}>
                                    {getResourceType(type, resource, t)}
                                </Text>
                            )}
                        </>
                    ) : (
                        <Text variant={textVariant}>
                            {type === 'file'
                                ? t('message.deleted-file', 'This file was deleted.')
                                : t('message.removed-link', 'This link was removed.')}
                        </Text>
                    )}
                </CustomStack>
                {resource.loading ? (
                    <CustomCircularProgress
                        aria-label={'loading'}
                        color={'primary'}
                        variant={resource.loadingValue ? 'determinate' : 'indeterminate'}
                        value={resource.loadingValue}
                    />
                ) : (
                    resource.active &&
                    options?.length && (
                        <Dropdown
                            id={`dropdown-${uuid()}`}
                            options={options}
                            rootComponent={
                                <IconButton
                                    aria-label={'more file options'}
                                    component={'div'}
                                    kind={'chevronDown'}
                                    size={'small'}
                                    variant={'square'}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                />
                            }
                            onSelect={(option) => onSelect(option, resource)}
                        />
                    )
                )}
            </Stack>
        </CustomButtonBase>
    );
});
